import { Navigate } from "react-router-dom";

import { UserDepartment } from "@/api/user/models";
import { useDepartmentRoute } from "@/hooks/common/use-department-route";
import { useUserStore } from "@/stores/use-user-store";
import { getUserInitialDepartmentRoute } from "@/utils/department-routes";
import { userHasDepartment } from "@/utils/user-positions";

interface DepartmentGuardProps {
  /**
   * Lista de departments permitidos para acessar esta rota.
   * Se o department da URL não estiver nesta lista, o usuário será redirecionado.
   */
  allowedDepartments: UserDepartment[];
  /**
   * Conteúdo a ser renderizado se o acesso for permitido.
   */
  children: React.ReactNode;
}

/**
 * Component que protege rotas exclusivas de departments específicos.
 *
 * Valida se o department presente na URL está na lista de departments permitidos.
 * Se não estiver, redireciona para a rota inicial do department do usuário autenticado.
 *
 * @example
 * // Rota exclusiva para Casting
 * {
 *   path: 'composites',
 *   element: (
 *     <DepartmentGuard allowedDepartments={[UserDepartment.CASTING]}>
 *       <CompositesPage />
 *     </DepartmentGuard>
 *   )
 * }
 *
 * @example
 * // Rota permitida para múltiplos departments
 * {
 *   path: 'relatorios',
 *   element: (
 *     <DepartmentGuard allowedDepartments={[UserDepartment.CASTING, UserDepartment.PHOTO_LAB]}>
 *       <RelatoriosPage />
 *     </DepartmentGuard>
 *   )
 * }
 */
export function DepartmentGuard({ allowedDepartments, children }: DepartmentGuardProps) {
  const { department } = useDepartmentRoute();
  const activeUser = useUserStore((state) => state.activeUser);

  const redirectTo = activeUser ? getUserInitialDepartmentRoute(activeUser) : "/login";

  // Se department é null (inválido), não está na lista de permitidos ou usuário não possui posição, redirecionar
  if (!department || !allowedDepartments.includes(department) || !userHasDepartment(activeUser, department)) {
    return <Navigate to={redirectTo ?? "/login"} replace />;
  }

  // Department válido, permitido e autorizado - renderizar conteúdo
  return <>{children}</>;
}
